import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import employerService from '../../../services/employerService';
import '../../../pages/Pages.css';
import './EmployerManagement.css';

const EmployerManagement = () => {
    const [employers, setEmployers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedEmployer, setSelectedEmployer] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [employerStats, setEmployerStats] = useState(null);
    const [filters, setFilters] = useState({
        search: '',
        role: '',
        status: '',
        employmentType: '',
        onlineStatus: ''
    });

    // Add Employer Form Data
    const [addFormData, setAddFormData] = useState({
        username: '',
        password: '',
        confirm_password: '',
        full_legal_name: '',
        contact_number: '',
        email: '',
        home_address: '',
        emergency_contact_name: '',
        emergency_contact_number: '',
        user_role: 'Cashier',
        hire_date: '',
        work_schedule: 'Shift-Based',
        employment_type: 'Hourly',
        hourly_wage: '',
        monthly_salary: '',
        bank_name: '',
        account_number: '',
        routing_number: '',
        status: 'Active',
        notes: ''
    });

    // Edit Employer Form Data
    const [editFormData, setEditFormData] = useState({
        username: '',
        full_legal_name: '',
        contact_number: '',
        email: '',
        home_address: '',
        emergency_contact_name: '',
        emergency_contact_number: '',
        user_role: 'Cashier',
        hire_date: '',
        work_schedule: 'Shift-Based',
        employment_type: 'Hourly',
        hourly_wage: '',
        monthly_salary: '',
        bank_name: '',
        account_number: '',
        routing_number: '',
        status: 'Active',
        notes: ''
    });

    useEffect(() => {
        loadEmployers();
    }, [currentPage]);

    const loadEmployers = async () => {
        try {
            setLoading(true);
            const response = await employerService.getAllEmployers(currentPage);
            setEmployers(response.data.data);
            setTotalPages(response.data.last_page);
        } catch (error) {
            Swal.fire('Error!', error.message || 'Failed to load employers', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleViewEmployer = async (employer) => {
        try {
            const [employerDetails, stats] = await Promise.all([
                employerService.getEmployerById(employer.id),
                employerService.getEmployerStats(employer.id)
            ]);

            setSelectedEmployer(employerDetails.data);
            setEmployerStats(stats.data);
            setShowViewModal(true);
        } catch (error) {
            Swal.fire('Error!', error.message || 'Failed to load employer details', 'error');
        }
    };

    const handleEditEmployer = async (employer) => {
        try {
            const response = await employerService.getEmployerById(employer.id);
            const employerData = response.data;

            setSelectedEmployer(employerData);
            setEditFormData({
                username: employerData.username,
                full_legal_name: employerData.full_legal_name,
                contact_number: employerData.contact_number,
                email: employerData.email,
                home_address: employerData.home_address || '',
                emergency_contact_name: employerData.emergency_contact_name || '',
                emergency_contact_number: employerData.emergency_contact_number || '',
                user_role: employerData.user_role,
                hire_date: employerData.hire_date ? employerData.hire_date.split('T')[0] : '',
                work_schedule: employerData.work_schedule,
                employment_type: employerData.employment_type,
                hourly_wage: employerData.hourly_wage || '',
                monthly_salary: employerData.monthly_salary || '',
                bank_name: employerData.bank_name || '',
                account_number: employerData.account_number || '',
                routing_number: employerData.routing_number || '',
                status: employerData.status,
                notes: employerData.notes || ''
            });
            setShowEditModal(true);
        } catch (error) {
            Swal.fire('Error!', error.message || 'Failed to load employer details', 'error');
        }
    };

    const handleUpdateEmployer = async (e) => {
        e.preventDefault();
        try {
            const formData = {
                ...editFormData,
                hourly_wage: editFormData.employment_type === 'Hourly' ? parseFloat(editFormData.hourly_wage) : null,
                monthly_salary: editFormData.employment_type === 'Salaried' ? parseFloat(editFormData.monthly_salary) : null
            };

            await employerService.updateEmployer(selectedEmployer.id, formData);
            Swal.fire('Success!', 'Employer updated successfully', 'success');
            setShowEditModal(false);
            loadEmployers();
        } catch (error) {
            Swal.fire('Error!', error.message || 'Failed to update employer', 'error');
        }
    };

    const handleAddEmployer = async (e) => {
        e.preventDefault();

        if (addFormData.password !== addFormData.confirm_password) {
            Swal.fire('Error!', 'Passwords do not match', 'error');
            return;
        }

        if (addFormData.password.length < 8) {
            Swal.fire('Error!', 'Password must be at least 8 characters long', 'error');
            return;
        }

        try {
            const formData = {
                ...addFormData,
                hourly_wage: addFormData.employment_type === 'Hourly' ? parseFloat(addFormData.hourly_wage) : null,
                monthly_salary: addFormData.employment_type === 'Salaried' ? parseFloat(addFormData.monthly_salary) : null
            };

            // Remove confirm_password from the data sent to API
            delete formData.confirm_password;

            await employerService.createEmployer(formData);
            Swal.fire('Success!', 'Employer created successfully', 'success');
            setShowAddModal(false);
            resetAddForm();
            loadEmployers();
        } catch (error) {
            Swal.fire('Error!', error.message || 'Failed to create employer', 'error');
        }
    };

    const resetAddForm = () => {
        setAddFormData({
            username: '',
            password: '',
            confirm_password: '',
            full_legal_name: '',
            contact_number: '',
            email: '',
            home_address: '',
            emergency_contact_name: '',
            emergency_contact_number: '',
            user_role: 'Cashier',
            hire_date: '',
            work_schedule: 'Shift-Based',
            employment_type: 'Hourly',
            hourly_wage: '',
            monthly_salary: '',
            bank_name: '',
            account_number: '',
            routing_number: '',
            status: 'Active',
            notes: ''
        });
    };

    const handleDeleteEmployer = (employer) => {
        Swal.fire({
            title: 'Are you sure?',
            text: `You want to delete ${employer.full_legal_name}? This action cannot be undone.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await employerService.deleteEmployer(employer.id);
                    Swal.fire('Deleted!', 'Employer has been deleted.', 'success');
                    loadEmployers();
                } catch (error) {
                    Swal.fire('Error!', error.message || 'Failed to delete employer', 'error');
                }
            }
        });
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const clearFilters = () => {
        setFilters({
            search: '',
            role: '',
            status: '',
            employmentType: '',
            onlineStatus: ''
        });
    };

    // In the filteredEmployers logic, add onlineStatus filter
    const filteredEmployers = employers.filter(employer => {
        const matchesSearch = !filters.search ||
            employer.full_legal_name.toLowerCase().includes(filters.search.toLowerCase()) ||
            employer.username.toLowerCase().includes(filters.search.toLowerCase()) ||
            employer.email.toLowerCase().includes(filters.search.toLowerCase());

        const matchesRole = !filters.role || employer.user_role === filters.role;
        const matchesStatus = !filters.status || employer.status === filters.status;
        const matchesEmploymentType = !filters.employmentType || employer.employment_type === filters.employmentType;

        // Add onlineStatus filter logic
        const matchesOnlineStatus = !filters.onlineStatus ||
            (filters.onlineStatus === 'online' && employer.is_online) ||
            (filters.onlineStatus === 'offline' && !employer.is_online);

        return matchesSearch && matchesRole && matchesStatus && matchesEmploymentType && matchesOnlineStatus;
    });

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'LKR'
        }).format(amount || 0);
    };

    if (loading) {
        return (
            <div className="page-container">
                <div className="page-header">
                    <h1>Employer Management</h1>
                    <p>Manage store employees and staff</p>
                </div>
                <div className="loading">Loading employers...</div>
            </div>
        );
    }

    return (
        <div className="page-container">
            <div className="page-header">
                <div className="header-content">
                    <div>
                        <h1>Employer Management</h1>
                        <p>Manage store employees and staff ({filteredEmployers.length} employers found)</p>
                    </div>
                    <button
                        className="btn btn-primary"
                        onClick={() => setShowAddModal(true)}
                    >
                        ➕ Add New Employer
                    </button>
                </div>
            </div>

            {/* Filters Section */}
            {/* Filters Section */}
            <div className="filters-section single-row">
                <div className="filters-row">
                    <div className="filter-group">
                        <input
                            type="text"
                            placeholder="Search by name, username, or email..."
                            value={filters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                            className="filter-input"
                        />
                    </div>

                    <div className="filter-group">
                        <select
                            value={filters.role}
                            onChange={(e) => handleFilterChange('role', e.target.value)}
                            className="filter-select"
                        >
                            <option value="">All Roles</option>
                            <option value="Cashier">Cashier</option>
                            <option value="Senior Cashier">Senior Cashier</option>
                            <option value="Manager">Manager</option>
                            <option value="Admin">Admin</option>
                        </select>
                    </div>

                    <div className="filter-group">
                        <select
                            value={filters.status}
                            onChange={(e) => handleFilterChange('status', e.target.value)}
                            className="filter-select"
                        >
                            <option value="">All Status</option>
                            <option value="Active">Active</option>
                            <option value="Inactive">Inactive</option>
                            <option value="On Leave">On Leave</option>
                            <option value="Terminated">Terminated</option>
                        </select>
                    </div>

                    <div className="filter-group">
                        <select
                            value={filters.employmentType}
                            onChange={(e) => handleFilterChange('employmentType', e.target.value)}
                            className="filter-select"
                        >
                            <option value="">All Types</option>
                            <option value="Hourly">Hourly</option>
                            <option value="Salaried">Salaried</option>
                        </select>
                    </div>

                    {/* Add this new filter group for online status */}
                    <div className="filter-group">
                        <select
                            value={filters.onlineStatus}
                            onChange={(e) => handleFilterChange('onlineStatus', e.target.value)}
                            className="filter-select"
                        >
                            <option value="">All Online Status</option>
                            <option value="online">Online</option>
                            <option value="offline">Offline</option>
                        </select>
                    </div>

                    <div className="filter-actions">
                        <button className="btn btn-clear" onClick={clearFilters} title="Clear Filters">
                            🗑️ Clear
                        </button>
                        <button className="btn btn-secondary" onClick={loadEmployers}>
                            🔄 Refresh
                        </button>
                    </div>
                </div>
            </div>

            <div className="page-content">
                <div className="employers-grid">
                    {filteredEmployers.map(employer => (
                        <div key={employer.id} className="employer-card">
                            <div className="employer-header">
                                <div className="employer-avatar">
                                    {employer.full_legal_name.split(' ').map(n => n[0]).join('').toUpperCase()}
                                </div>
                                <div className="employer-basic-info">
                                    <h3 className="employer-name">{employer.full_legal_name}</h3>
                                    <p className="employer-username">@{employer.username}</p>
                                    <div className="employer-meta">
                                        <span className={`status-badge ${employer.status.toLowerCase().replace(' ', '-')}`}>
                                            {employer.status}
                                        </span>
                                        <span className="role-badge">{employer.user_role}</span>
                                        {employer.is_online ? (
                                            <span className="online-indicator" title="Online">🟢</span>
                                        ) : (
                                            <span className="offline-indicator" title="Offline">🔴</span>
                                        )}

                                    </div>
                                </div>
                            </div>

                            <div className="employer-details">
                                <div className="detail-row">
                                    <span className="label">Employee ID:</span>
                                    <span className="value">{employer.employee_id}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="label">Email:</span>
                                    <span className="value">{employer.email}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="label">Contact:</span>
                                    <span className="value">{employer.contact_number}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="label">Hire Date:</span>
                                    <span className="value">{formatDate(employer.hire_date)}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="label">Employment:</span>
                                    <span className="value">
                                        {employer.employment_type} • {employer.work_schedule}
                                    </span>
                                </div>
                                <div className="detail-row">
                                    <span className="label">Compensation:</span>
                                    <span className="value">
                                        {employer.employment_type === 'Hourly'
                                            ? formatCurrency(employer.hourly_wage) + '/hr'
                                            : formatCurrency(employer.monthly_salary) + '/mo'
                                        }
                                    </span>
                                </div>
                            </div>

                            <div className="employer-actions">
                                <button
                                    className="btn btn-view"
                                    onClick={() => handleViewEmployer(employer)}
                                    title="View Details"
                                >
                                    👁️ View
                                </button>
                                <button
                                    className="btn btn-edit"
                                    onClick={() => handleEditEmployer(employer)}
                                    title="Edit Employer"
                                >
                                    ✏️ Edit
                                </button>
                                <button
                                    className="btn btn-delete"
                                    onClick={() => handleDeleteEmployer(employer)}
                                    title="Delete Employer"
                                >
                                    🗑️ Delete
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                {filteredEmployers.length === 0 && (
                    <div className="no-data">
                        <div className="no-data-icon">👥</div>
                        <h3>No Employers Found</h3>
                        <p>{filters.search || filters.role || filters.status || filters.employmentType
                            ? 'No employers match your current filters.'
                            : 'Get started by adding your first employer.'
                        }</p>
                        <button
                            className="btn btn-primary"
                            onClick={() => setShowAddModal(true)}
                        >
                            ➕ Add New Employer
                        </button>
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="pagination">
                        <button
                            className="btn btn-secondary"
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                        >
                            ← Previous
                        </button>

                        <span className="page-info">
                            Page {currentPage} of {totalPages}
                        </span>

                        <button
                            className="btn btn-secondary"
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                        >
                            Next →
                        </button>
                    </div>
                )}
            </div>

            {/* Add Employer Modal */}
            {showAddModal && (
                <AddEditEmployerModal
                    mode="add"
                    formData={addFormData}
                    setFormData={setAddFormData}
                    onClose={() => { setShowAddModal(false); resetAddForm(); }}
                    onSubmit={handleAddEmployer}
                />
            )}

            {/* Edit Employer Modal */}
            {showEditModal && (
                <AddEditEmployerModal
                    mode="edit"
                    formData={editFormData}
                    setFormData={setEditFormData}
                    onClose={() => setShowEditModal(false)}
                    onSubmit={handleUpdateEmployer}
                    employer={selectedEmployer}
                />
            )}

            {/* View Employer Modal */}
            {showViewModal && selectedEmployer && employerStats && (
                <ViewEmployerModal
                    employer={selectedEmployer}
                    stats={employerStats}
                    onClose={() => setShowViewModal(false)}
                    formatDate={formatDate}
                    formatCurrency={formatCurrency}
                />
            )}
        </div>
    );
};

// Add/Edit Employer Modal Component
const AddEditEmployerModal = ({ mode, formData, setFormData, onClose, onSubmit, employer }) => {
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    return (
        <div className="modal-overlay">
            <div className="modal modal-extra-large">
                <div className="modal-header">
                    <h3>{mode === 'add' ? 'Add New Employer' : `Edit Employer - ${employer?.full_legal_name}`}</h3>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>
                <form onSubmit={onSubmit}>
                    <div className="modal-body">
                        <div className="employer-form">
                            {/* Basic Information Section */}
                            <div className="form-section">
                                <h4>Basic Information</h4>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>Username *</label>
                                        <input
                                            type="text"
                                            name="username"
                                            value={formData.username}
                                            onChange={handleInputChange}
                                            required
                                            placeholder="Enter username"
                                        />
                                    </div>

                                    {mode === 'add' && (
                                        <>
                                            <div className="form-group">
                                                <label>Password *</label>
                                                <input
                                                    type="password"
                                                    name="password"
                                                    value={formData.password}
                                                    onChange={handleInputChange}
                                                    required
                                                    minLength="8"
                                                    placeholder="Enter password"
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Confirm Password *</label>
                                                <input
                                                    type="password"
                                                    name="confirm_password"
                                                    value={formData.confirm_password}
                                                    onChange={handleInputChange}
                                                    required
                                                    minLength="8"
                                                    placeholder="Confirm password"
                                                />
                                            </div>
                                        </>
                                    )}

                                    <div className="form-group">
                                        <label>Full Legal Name *</label>
                                        <input
                                            type="text"
                                            name="full_legal_name"
                                            value={formData.full_legal_name}
                                            onChange={handleInputChange}
                                            required
                                            placeholder="Enter full legal name"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Contact Number *</label>
                                        <input
                                            type="tel"
                                            name="contact_number"
                                            value={formData.contact_number}
                                            onChange={handleInputChange}
                                            required
                                            placeholder="Enter contact number"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Email *</label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            required
                                            placeholder="Enter email address"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>User Role *</label>
                                        <select
                                            name="user_role"
                                            value={formData.user_role}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            <option value="Cashier">Cashier</option>
                                            <option value="Senior Cashier">Senior Cashier</option>
                                            <option value="Manager">Manager</option>
                                            <option value="Admin">Admin</option>
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label>Hire Date *</label>
                                        <input
                                            type="date"
                                            name="hire_date"
                                            value={formData.hire_date}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Employment Details Section */}
                            <div className="form-section">
                                <h4>Employment Details</h4>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>Work Schedule *</label>
                                        <select
                                            name="work_schedule"
                                            value={formData.work_schedule}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            <option value="Full-Time">Full-Time</option>
                                            <option value="Part-Time">Part-Time</option>
                                            <option value="Shift-Based">Shift-Based</option>
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label>Employment Type *</label>
                                        <select
                                            name="employment_type"
                                            value={formData.employment_type}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            <option value="Hourly">Hourly</option>
                                            <option value="Salaried">Salaried</option>
                                        </select>
                                    </div>

                                    {formData.employment_type === 'Hourly' && (
                                        <div className="form-group">
                                            <label>Hourly Wage ($) *</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                name="hourly_wage"
                                                value={formData.hourly_wage}
                                                onChange={handleInputChange}
                                                required
                                                min="0"
                                                placeholder="0.00"
                                            />
                                        </div>
                                    )}

                                    {formData.employment_type === 'Salaried' && (
                                        <div className="form-group">
                                            <label>Monthly Salary ($) *</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                name="monthly_salary"
                                                value={formData.monthly_salary}
                                                onChange={handleInputChange}
                                                required
                                                min="0"
                                                placeholder="0.00"
                                            />
                                        </div>
                                    )}

                                    <div className="form-group">
                                        <label>Status</label>
                                        <select
                                            name="status"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                        >
                                            <option value="Active">Active</option>
                                            <option value="Inactive">Inactive</option>
                                            <option value="On Leave">On Leave</option>
                                            <option value="Terminated">Terminated</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Contact & Emergency Information */}
                            <div className="form-section">
                                <h4>Contact & Emergency Information</h4>
                                <div className="form-grid">
                                    <div className="form-group full-width">
                                        <label>Home Address</label>
                                        <textarea
                                            name="home_address"
                                            value={formData.home_address}
                                            onChange={handleInputChange}
                                            rows="3"
                                            placeholder="Enter home address"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Emergency Contact Name</label>
                                        <input
                                            type="text"
                                            name="emergency_contact_name"
                                            value={formData.emergency_contact_name}
                                            onChange={handleInputChange}
                                            placeholder="Enter emergency contact name"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Emergency Contact Number</label>
                                        <input
                                            type="tel"
                                            name="emergency_contact_number"
                                            value={formData.emergency_contact_number}
                                            onChange={handleInputChange}
                                            placeholder="Enter emergency contact number"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Banking Information */}
                            <div className="form-section">
                                <h4>Banking Information</h4>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>Bank Name</label>
                                        <input
                                            type="text"
                                            name="bank_name"
                                            value={formData.bank_name}
                                            onChange={handleInputChange}
                                            placeholder="Enter bank name"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Account Number</label>
                                        <input
                                            type="text"
                                            name="account_number"
                                            value={formData.account_number}
                                            onChange={handleInputChange}
                                            placeholder="Enter account number"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Routing Number</label>
                                        <input
                                            type="text"
                                            name="routing_number"
                                            value={formData.routing_number}
                                            onChange={handleInputChange}
                                            placeholder="Enter routing number"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Additional Information */}
                            <div className="form-section">
                                <h4>Additional Information</h4>
                                <div className="form-group full-width">
                                    <label>Notes</label>
                                    <textarea
                                        name="notes"
                                        value={formData.notes}
                                        onChange={handleInputChange}
                                        rows="3"
                                        placeholder="Enter any additional notes..."
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary">
                            {mode === 'add' ? 'Create Employer' : 'Update Employer'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// View Employer Modal Component
const ViewEmployerModal = ({ employer, stats, onClose, formatDate, formatCurrency }) => {
    return (
        <div className="modal-overlay">
            <div className="modal modal-extra-large">
                <div className="modal-header">
                    <h3>Employer Details - {employer.full_legal_name}</h3>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>
                <div className="modal-body">
                    <div className="employer-detail-view">
                        {/* Header with Basic Info */}
                        <div className="employer-detail-header">
                            <div className="employer-avatar large">
                                {employer.full_legal_name.split(' ').map(n => n[0]).join('').toUpperCase()}
                            </div>
                            <div className="employer-header-info">
                                <h2>{employer.full_legal_name}</h2>
                                <p className="employee-id">Employee ID: {employer.employee_id}</p>
                                <div className="employer-tags">
                                    <span className={`status-badge ${employer.status.toLowerCase().replace(' ', '-')}`}>
                                        {employer.status}
                                    </span>
                                    <span className="role-badge">{employer.user_role}</span>
                                    <span className="type-badge">{employer.employment_type}</span>
                                    {employer.is_online && (
                                        <span className="online-badge">🟢 Online</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Statistics Cards */}
                        <div className="stats-cards-row">
                            <div className="stat-card">
                                <div className="stat-icon">⏰</div>
                                <div className="stat-content">
                                    <h4>{stats.today_hours}h</h4>
                                    <p>Today</p>
                                </div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-icon">📅</div>
                                <div className="stat-content">
                                    <h4>{stats.weekly_hours}h</h4>
                                    <p>This Week</p>
                                </div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-icon">💰</div>
                                <div className="stat-content">
                                    <h4>{formatCurrency(stats.current_period_pay)}</h4>
                                    <p>Current Pay</p>
                                </div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-icon">👥</div>
                                <div className="stat-content">
                                    <h4>{employer.work_schedule}</h4>
                                    <p>Schedule</p>
                                </div>
                            </div>
                        </div>

                        {/* Detailed Information Sections */}
                        <div className="detail-sections">
                            {/* Personal Information */}
                            <div className="detail-section">
                                <h4>Personal Information</h4>
                                <div className="detail-grid">
                                    <div className="detail-item">
                                        <label>Username:</label>
                                        <span>@{employer.username}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Email:</label>
                                        <span>{employer.email}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Contact Number:</label>
                                        <span>{employer.contact_number}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Home Address:</label>
                                        <span>{employer.home_address || 'Not provided'}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Employment Information */}
                            <div className="detail-section">
                                <h4>Employment Information</h4>
                                <div className="detail-grid">
                                    <div className="detail-item">
                                        <label>Hire Date:</label>
                                        <span>{formatDate(employer.hire_date)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Work Schedule:</label>
                                        <span>{employer.work_schedule}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Employment Type:</label>
                                        <span>{employer.employment_type}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Compensation:</label>
                                        <span>
                                            {employer.employment_type === 'Hourly'
                                                ? `${formatCurrency(employer.hourly_wage)}/hour`
                                                : `${formatCurrency(employer.monthly_salary)}/month`
                                            }
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Emergency Contact */}
                            {employer.emergency_contact_name && (
                                <div className="detail-section">
                                    <h4>Emergency Contact</h4>
                                    <div className="detail-grid">
                                        <div className="detail-item">
                                            <label>Contact Name:</label>
                                            <span>{employer.emergency_contact_name}</span>
                                        </div>
                                        <div className="detail-item">
                                            <label>Contact Number:</label>
                                            <span>{employer.emergency_contact_number}</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Banking Information */}
                            {employer.bank_name && (
                                <div className="detail-section">
                                    <h4>Banking Information</h4>
                                    <div className="detail-grid">
                                        <div className="detail-item">
                                            <label>Bank Name:</label>
                                            <span>{employer.bank_name}</span>
                                        </div>
                                        <div className="detail-item">
                                            <label>Account Number:</label>
                                            <span>{employer.account_number}</span>
                                        </div>
                                        <div className="detail-item">
                                            <label>Routing Number:</label>
                                            <span>{employer.routing_number}</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Time Logs */}
                            {employer.time_logs && employer.time_logs.length > 0 && (
                                <div className="detail-section">
                                    <h4>Recent Time Logs</h4>
                                    <div className="time-logs-table">
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th>Date</th>
                                                    <th>Clock In</th>
                                                    <th>Clock Out</th>
                                                    <th>Total Hours</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {employer.time_logs.slice(0, 5).map(log => (
                                                    <tr key={log.id}>
                                                        <td>{formatDate(log.clock_in_at)}</td>
                                                        <td>{new Date(log.clock_in_at).toLocaleTimeString()}</td>
                                                        <td>{log.clock_out_at ? new Date(log.clock_out_at).toLocaleTimeString() : '-'}</td>
                                                        <td>{log.total_hours}h</td>
                                                        <td>
                                                            <span className={`status-badge ${log.status.toLowerCase().replace(' ', '-')}`}>
                                                                {log.status}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Additional Notes */}
                            {employer.notes && (
                                <div className="detail-section">
                                    <h4>Additional Notes</h4>
                                    <div className="notes-content">
                                        {employer.notes}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EmployerManagement;